import { cn } from '@/lib/cn';
import type { OnboardingStep } from '@innermate/shared';
import { Check, Database, HardDrive, type LucideIcon, ShieldCheck, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

interface StepDef {
  key: Exclude<OnboardingStep, 'done'> | 'finish';
  label: string;
  hint: string;
  icon: LucideIcon;
}

const STEPS: StepDef[] = [
  { key: 'db', label: '시스템 DB', hint: 'PostgreSQL 접속', icon: Database },
  { key: 'admin', label: '관리자 계정', hint: '첫 admin 생성', icon: ShieldCheck },
  { key: 'llm', label: 'LLM 엔드포인트', hint: 'OpenAI 호환', icon: Sparkles },
  { key: 'storage', label: '오브젝트 스토리지', hint: 'S3 호환 (선택)', icon: HardDrive },
  { key: 'finish', label: '완료', hint: '설정 마무리', icon: Check },
];

interface OnboardingShellProps {
  current: StepDef['key'];
  title: string;
  description?: string;
  children: ReactNode;
}

export function OnboardingShell({ current, title, description, children }: OnboardingShellProps) {
  const currentIdx = STEPS.findIndex((s) => s.key === current);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r border-border bg-surface-muted/40 px-5 py-8">
        <div className="mb-8 flex items-center gap-2">
          <div className="size-8 rounded-lg bg-accent grid place-items-center">
            <Sparkles className="size-4 text-accent-foreground" />
          </div>
          <span className="text-base font-semibold tracking-tight">innermate</span>
        </div>

        <div className="text-xs uppercase tracking-wider text-foreground-muted mb-3">최초 설정</div>
        <ol className="space-y-1">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isCurrent = idx === currentIdx;
            const isDone = idx < currentIdx;
            return (
              <li key={step.key}>
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                    isCurrent
                      ? 'bg-surface text-foreground border border-border shadow-[var(--shadow-card)]'
                      : isDone
                        ? 'text-foreground-muted'
                        : 'text-foreground-muted/70',
                  )}
                >
                  <span
                    className={cn(
                      'size-6 rounded-full grid place-items-center text-xs',
                      isCurrent
                        ? 'bg-accent text-accent-foreground'
                        : isDone
                          ? 'bg-surface-muted text-foreground'
                          : 'bg-transparent border border-border text-foreground-muted',
                    )}
                  >
                    {isDone ? <Check className="size-3.5" /> : <Icon className="size-3.5" />}
                  </span>
                  <div className="min-w-0">
                    <div className="font-medium leading-tight">{step.label}</div>
                    <div className="text-xs text-foreground-muted leading-tight">{step.hint}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-6 py-16">
          <header className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {description ? (
              <p className="mt-2 text-sm text-foreground-muted">{description}</p>
            ) : null}
          </header>
          <div className="bg-surface border border-border rounded-2xl shadow-[var(--shadow-card)] p-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
